import { memo, useState, useCallback, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { generateId } from '../utils/helpers';

export const Recordings = memo(({ recordings, setRecordings, content }) => {
  const [show, setShow] = useState(false);
  const [date, setDate] = useState('');
  const [vids, setVids] = useState([]);
  const [notes, setNotes] = useState('');

  const ready = useMemo(() =>
    content.filter(c => ['approved', 'scripting', 'recording'].includes(c.status)),
    [content]
  );

  const upcoming = useMemo(() =>
    recordings
      .filter(r => new Date(r.date) >= new Date().setHours(0, 0, 0, 0))
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [recordings]
  );

  const submit = useCallback(() => {
    if (date) {
      setRecordings([...recordings, { id: generateId(), date, vids, notes }]);
      setShow(false);
      setDate('');
      setVids([]);
      setNotes('');
    }
  }, [date, vids, notes, recordings, setRecordings]);

  const deleteRecording = useCallback((id) => {
    setRecordings(recordings.filter(r => r.id !== id));
  }, [recordings, setRecordings]);

  const toggleVid = useCallback((vidId) => {
    setVids(vids.includes(vidId) ? vids.filter(x => x !== vidId) : [...vids, vidId]);
  }, [vids]);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl md:text-2xl font-bold text-slate-800">Recording Schedule</h2>
        <button
          onClick={() => setShow(true)}
          className="flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 text-sm md:text-base flex-shrink-0"
        >
          <Plus className="w-5 h-5" /> Schedule
        </button>
      </div>

      {/* Ready to Record */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-bold text-lg text-slate-800 mb-4">Ready to Record ({ready.length})</h3>
        {ready.length === 0 ? (
          <p className="text-slate-400 py-4">No content ready</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {ready.map(c => (
              <div key={c.id} className="px-4 py-3 bg-purple-50 border border-purple-200 rounded-xl">
                <p className="font-semibold text-purple-800">{c.title}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sessions */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-bold text-lg text-slate-800 mb-4">Upcoming Sessions</h3>
        {upcoming.length === 0 ? (
          <p className="text-slate-400 text-center py-10">No sessions scheduled</p>
        ) : (
          <div className="space-y-4">
            {upcoming.map(s => (
              <div key={s.id} className="p-5 bg-purple-50 border border-purple-200 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-purple-900">
                    {new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </h4>
                  <button
                    onClick={() => deleteRecording(s.id)}
                    className="p-1.5 text-purple-400 hover:text-red-500"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-purple-600 font-medium">{s.vids?.length || 0} videos planned</p>
                {s.vids?.map((vid, i) => {
                  const v = content.find(c => c.id === vid);
                  return v && <p key={i} className="text-sm text-purple-700 mt-1">• {v.title}</p>;
                })}
                {s.notes && <p className="text-sm text-purple-500 mt-2 italic">{s.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {show && (
        <RecordingModal
          show={show}
          setShow={setShow}
          date={date}
          setDate={setDate}
          ready={ready}
          vids={vids}
          toggleVid={toggleVid}
          notes={notes}
          setNotes={setNotes}
          submit={submit}
        />
      )}
    </div>
  );
});

Recordings.displayName = 'Recordings';

const RecordingModal = memo(({ show, setShow, date, setDate, ready, vids, toggleVid, notes, setNotes, submit }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShow(false)}>
    <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
      <h3 className="font-bold text-xl text-slate-800 mb-5">Schedule Recording</h3>
      <div className="space-y-4">
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-violet-400 focus:outline-none"
        />
        <div className="max-h-48 overflow-y-auto space-y-2">
          {ready.map(c => (
            <label
              key={c.id}
              className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100"
            >
              <input
                type="checkbox"
                checked={vids.includes(c.id)}
                onChange={() => toggleVid(c.id)}
                className="w-5 h-5 rounded"
              />
              <span className="font-medium">{c.title}</span>
            </label>
          ))}
          {ready.length === 0 && <p className="text-slate-400 py-4 text-center">No content ready</p>}
        </div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-violet-400 focus:outline-none"
          rows={2}
          placeholder="Notes (optional)"
        />
      </div>
      <div className="flex gap-3 mt-6">
        <button
          onClick={() => setShow(false)}
          className="flex-1 px-4 py-3 bg-slate-100 text-slate-600 rounded-xl font-semibold hover:bg-slate-200"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!date}
          className="flex-1 px-4 py-3 bg-violet-600 text-white rounded-xl font-semibold disabled:opacity-50 hover:bg-violet-700"
        >
          Schedule
        </button>
      </div>
    </div>
  </div>
));

RecordingModal.displayName = 'RecordingModal';
